import { useCallback, useMemo } from 'react'
import useAvailableActivities from './useAvailableActivities'
import useAvailableCatalogs from './useAvailableCatalogs'
import { TypeChamp } from '../../../enums/TypeChamp'

/**
 * Activité (fields.Descr) -> TypeChamp "ManualEntry" correspondant : ces 3 types ne dépendent
 * pas des catalogues mais de la présence de l'activité chez le groupe.
 */
const ACTIVITY_DESCR_TO_TYPE_CHAMP: Record<string, TypeChamp> = {
    Automobile: TypeChamp.AutomobileManualEntry,
    Moto: TypeChamp.MotorcycleManualEntry,
    Loisirs: TypeChamp.RecreationalManualEntry,
}

/**
 * Tous les TypeChamp dont la disponibilité dépend des catalogues/activités du groupe. Un
 * TypeChamp absent de cet ensemble est toujours considéré comme disponible (Particulier/Pro,
 * VN/VO/VDL/APV, Automobile/Motorcycle... ne sont pas filtrés).
 */
const GATED_TYPE_CHAMPS = new Set<TypeChamp>([
    TypeChamp.Autovista,
    TypeChamp.Kifal,
    TypeChamp.RecreationalReference,
    TypeChamp.MotorcycleReference,
    TypeChamp.Autobiz,
    TypeChamp.AutomobileManualEntry,
    TypeChamp.MotorcycleManualEntry,
    TypeChamp.RecreationalManualEntry,
])

interface UseAvailableTypeChampsReturn {
    isTypeChampAvailable: (type: string | null) => boolean
    loading: boolean
}

/**
 * Combine catalogues (useAvailableCatalogs) et activités (useAvailableActivities) du groupe en
 * une seule règle de disponibilité par TypeChamp, utilisée pour n'afficher que les onglets
 * "type" pertinents dans un bloc.
 */
const useAvailableTypeChamps = (): UseAvailableTypeChampsReturn => {
    const { activites, loading: loadingActivites } = useAvailableActivities()
    const { availableTypeChamps: availableFromCatalogs, loading: loadingCatalogs } = useAvailableCatalogs()

    const availableTypeChamps = useMemo(() => {
        const set = new Set<TypeChamp>(availableFromCatalogs)
        activites.forEach(activite => {
            const typeChamp = ACTIVITY_DESCR_TO_TYPE_CHAMP[activite.Descr]
            if (typeChamp) set.add(typeChamp)
        })
        return set
    }, [activites, availableFromCatalogs])

    const isTypeChampAvailable = useCallback((type: string | null) => {
        if (!type || !GATED_TYPE_CHAMPS.has(type as TypeChamp)) return true
        return availableTypeChamps.has(type as TypeChamp)
    }, [availableTypeChamps])

    return { isTypeChampAvailable, loading: loadingActivites || loadingCatalogs }
}

export default useAvailableTypeChamps
